import {
  P3Configuration as IP3Configuration,
  P3ConfigurationCategory,
  P3ConfigurationRegion
} from 'models/interfaces/P3Configurations';
import { CONSTANTS } from '@tpco/tpco-saturn-types';
import { P3Campaign } from 'models/interfaces';
import uuid from 'uuid/v4';

interface P3ConfigurationOptions {
  configurationId?: string;
  campaignId?: string;
  tgramsId?: string;
}

class P3Configuration implements IP3Configuration {
  public p3_configuration_id: string = '';
  public p3_campaign_id: string = '';
  public tgrams_id?: string;
  public name: string = '';
  public description?: string;
  public rank_points: number = 0;
  public coverage_areas: number = 0;
  public configuration_status_id: number = CONSTANTS.CONFIGURATION_STATUS.UNCONFIGURED;
  public preview_ads: number = 0;
  public program_value: string = '0.0000';
  public start_date?: string;
  public end_date?: string;
  public created_date?: string;
  public categories: P3ConfigurationCategory[] = [];
  public regions: P3ConfigurationRegion[] = [];

  constructor(options?: P3ConfigurationOptions) {
    // only set these values if passed in from options, else default
    if (options) {
      this.p3_configuration_id = options.configurationId || '';
      this.p3_campaign_id = options.campaignId || '';
      this.tgrams_id = options.tgramsId;
    }
  }

  /**
   * Configuration can be considered new if it hasn't been assigned an id yet
   */
  public static configurationIsNew = (p3Configuration: IP3Configuration) =>
    !p3Configuration.p3_configuration_id;

  public static makeP3ConfigurationInput(p3Configuration: IP3Configuration) {
    // basically build an object with all the values except created_date
    const postModel: IP3Configuration = {
      p3_configuration_id: p3Configuration.p3_configuration_id,
      p3_campaign_id: p3Configuration.p3_campaign_id,
      configuration_status_id: p3Configuration.configuration_status_id,
      tgrams_id: p3Configuration.tgrams_id,
      name: p3Configuration.name,
      description: p3Configuration.description,
      rank_points: p3Configuration.rank_points,
      coverage_areas: p3Configuration.coverage_areas,
      preview_ads: p3Configuration.preview_ads,
      program_value: p3Configuration.program_value,
      start_date: p3Configuration.start_date,
      end_date: p3Configuration.end_date,
      regions: p3Configuration.regions,
      categories: p3Configuration.categories
    };

    return postModel;
  }

  public static fromP3Campaign(
    p3Campaign: P3Campaign,
    { configurationId, campaignId, tgramsId }: P3ConfigurationOptions
  ) {
    // build P3Configuration from p3Campaign
    const p3Configuration = new P3Configuration();

    /**
     * if user passed in config options, use those first,
     * then fall back to p3Campaign data or defaults
     */
    p3Configuration.p3_configuration_id =
      configurationId || p3Campaign.p3_configuration_id || uuid();

    p3Configuration.tgrams_id =
      tgramsId || (p3Campaign.p3_configuration && p3Campaign.p3_configuration.tgrams_id);

    p3Configuration.p3_campaign_id = campaignId || p3Campaign.p3_campaign_id || '';

    // use the p3Campaign data to set these fields
    p3Configuration.name = p3Campaign.name;
    p3Configuration.rank_points = 0; // set the rank points to whatever was set on the campaign
    p3Configuration.start_date = p3Campaign.start_date;
    p3Configuration.end_date = p3Campaign.end_date;

    // use defaults for these fields
    p3Configuration.coverage_areas = 0;
    p3Configuration.preview_ads = 0;

    return p3Configuration;
  }

  public static newCompanyTemplate(tgramsId: string) {
    // return a new P3Configuration object w/ tgramsId and random uuid for configurationId
    return new P3Configuration({ tgramsId, configurationId: uuid() });
  }

  public static newUserTemplate() {
    // return a new P3Configuration object w/ random uuid for configurationId
    return new P3Configuration({ configurationId: uuid() });
  }

  public setNewConfigurationId() {
    this.p3_configuration_id = uuid();
    return this;
  }

  public makeP3ConfigurationInput() {
    // basically build an object with all the values except created_date
    const postModel: IP3Configuration = {
      p3_configuration_id: this.p3_configuration_id,
      p3_campaign_id: this.p3_campaign_id,
      configuration_status_id: this.configuration_status_id,
      tgrams_id: this.tgrams_id,
      name: this.name,
      description: this.description,
      rank_points: this.rank_points,
      coverage_areas: this.coverage_areas,
      preview_ads: this.preview_ads,
      program_value: this.program_value,
      start_date: this.start_date,
      end_date: this.end_date,
      regions: this.regions,
      categories: this.categories
    };

    return postModel;
  }
}

export default P3Configuration;
